import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { Language, Translations } from './types';
import { en } from './translations/en';
import { ru } from './translations/ru';
import { es } from './translations/es';

const translations: Record<Language, Translations> = {
  en,
  ru,
  es,
};

// Determine if a language code should use a specific supported language
export const normalizeLanguage = (code: string | undefined | null): Language => {
  if (!code) return 'ru';
  
  const lowerCode = code.toLowerCase();
  
  // Russian
  if (lowerCode === 'ru' || lowerCode.startsWith('ru-')) {
    return 'ru';
  }
  
  // Spanish
  if (lowerCode === 'es' || lowerCode.startsWith('es-')) {
    return 'es';
  }
  
  // Default to English
  return 'en';
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  userLanguage?: string | null;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  userLanguage,
}) => {
  const [language, setLanguageState] = useState<Language>(() => 
    normalizeLanguage(userLanguage)
  );

  // Update language when userLanguage prop changes (e.g., after user data loads)
  useEffect(() => {
    if (userLanguage) {
      const normalized = normalizeLanguage(userLanguage);
      setLanguageState(normalized);
    }
  }, [userLanguage]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const t = useMemo(() => translations[language], [language]);

  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
  }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Shorthand hook for just translations
export const useTranslation = () => {
  const { t } = useLanguage();
  return t;
};
